import * as React from "react";
import * as Select from "@radix-ui/react-select";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface SelectOption {
  label: string;
  value: string | number;
}

interface MultiSelectProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Select.Root>, "value" | "onValueChange"> {
  label?: string;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  error?: string;
  value: string[];
  onValueChange: (value: string[]) => void;
  id?: string;
}

const MultiSelect = React.forwardRef<React.ElementRef<typeof Select.Trigger>, MultiSelectProps>(
  (
    {
      className,
      label,
      options,
      placeholder = "Select options",
      error,
      value,
      onValueChange,
      id,
      ...props
    },
    ref
  ) => {
    return (
      <div>
        {label && (
          <label
            htmlFor={id}
            className="mb-1.5 cursor-pointer text-[15px] font-normal text-system-disabled"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <Select.Root
            {...props}
            onValueChange={newValue => {
              const currentValue = value || [];
              const updatedValue = currentValue.includes(newValue)
                ? currentValue.filter(v => v !== newValue)
                : [...currentValue, newValue];
              onValueChange(updatedValue);
            }}
          >
            <Select.Trigger
              ref={ref}
              id={id}
              className={cn(
                "flex min-h-[42px] w-full items-start justify-between rounded-lg border bg-system-graphite px-4 py-2.5",
                "text-base text-system-disabled focus:outline-none focus:ring-0",
                error
                  ? "border-system-error focus:border-system-error"
                  : "border-[#61646C66] focus:border-system-primary",
                className
              )}
            >
              <div className="truncate">
                {value?.length === 0 ? (
                  <span className="text-system-titanium">{placeholder}</span>
                ) : (
                  <span className="text-system-disabled">{value?.join(", ")}</span>
                )}
              </div>
              <Select.Icon>
                <ChevronDown className="h-5 w-5" />
              </Select.Icon>
            </Select.Trigger>

            <Select.Portal>
              <Select.Content
                className="w-[--radix-select-trigger-width] overflow-hidden rounded-lg bg-system-graphite p-1"
                position="popper"
                sideOffset={8}
              >
                <Select.Viewport>
                  {options.map(option => (
                    <Select.Item
                      key={option.value}
                      value={String(option.value)}
                      className={cn(
                        "flex cursor-pointer items-center justify-between rounded-md px-4 py-2 outline-none",
                        "data-[highlighted]:bg-system-primary data-[highlighted]:text-white",
                        value?.includes(String(option.value))
                          ? "text-system-primary"
                          : "text-system-disabled"
                      )}
                    >
                      <Select.ItemText>{option.label}</Select.ItemText>
                      {value?.includes(String(option.value)) && (
                        <Select.ItemIndicator>
                          <Check className="h-4 w-4" />
                        </Select.ItemIndicator>
                      )}
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>
        {error && <p className="mt-1 text-[13px] text-system-error">{error}</p>}
      </div>
    );
  }
);

MultiSelect.displayName = "MultiSelect";

export default MultiSelect;
